import React, { useState } from "react";
import { api } from "../../lib/api";
import { Mail, Bell, FileText, ShieldCheck, FileSignature, Receipt, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Quick-action email rail on Loan Detail.
 * Each button fires an existing templated automation via /comms/send-templated,
 * pre-populated with the loan's attorney email + full merge context.
 * If no attorney email exists, opens a lightweight prompt to enter one.
 */
const ACTIONS = [
  { key: "reminder",         label: "Send reminder",             Icon: Bell,          template: "attorney.reminder_generic",       needsInfo: false, testid: "action-send-reminder" },
  { key: "docs",             label: "Request documents",         Icon: FileText,      template: "attorney.request_documents",      needsInfo: true,  testid: "action-request-docs" },
  { key: "verification",     label: "Request verification",      Icon: ShieldCheck,   template: "attorney.request_verification",   needsInfo: false, testid: "action-request-verification" },
  { key: "funding-docs",     label: "Send funding docs",         Icon: FileSignature, template: "attorney.send_funding_documents", needsInfo: false, testid: "action-send-funding-docs" },
  { key: "payoff-attorney",  label: "Send payoff (attorney)",    Icon: Receipt,       template: "attorney.payoff_send",            needsInfo: false, testid: "action-send-payoff-attorney" },
];

export default function LoanActionRail({ loan, onSent }) {
  const [busy, setBusy] = useState(null);
  const [askInfo, setAskInfo] = useState(null);
  const [infoText, setInfoText] = useState("");
  const [askEmail, setAskEmail] = useState(null);
  const [emailInput, setEmailInput] = useState("");

  const fire = async (action, extraContext = {}, recipientOverride = null) => {
    const recipient = recipientOverride || loan?.attorney_email;
    if (!recipient) {
      // No attorney on file → prompt for email inline
      setAskEmail(action);
      return;
    }
    setBusy(action.key);
    try {
      const { data } = await api.post("/comms/send-templated", {
        template_key: action.template,
        recipient,
        loan_id: loan?.loan_id,
        firm_id: loan?.firm_id,
        attorney_email: recipient,
        context_override: extraContext,
      });
      if (data.status === "sent") toast.success(`${action.label} sent to ${recipient}`);
      else if (data.status === "skipped") toast.warning(`Skipped: ${data.reason || "config missing"}`);
      else if (data.status === "suppressed") toast.error(`Blocked: recipient suppressed`);
      else toast.error(`Failed: ${data.error || "unknown"}`);
      onSent?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setBusy(null);
    }
  };

  const handleClick = (action) => {
    if (action.needsInfo) {
      setAskInfo(action);
      setInfoText("");
      return;
    }
    fire(action);
  };

  return (
    <div className="border border-[#E6EBF1] rounded-xl bg-white p-4" data-testid="loan-action-rail">
      <div className="flex items-center gap-2 mb-3">
        <Mail className="w-4 h-4 text-[#0066FF]" />
        <span className="text-xs uppercase tracking-wider text-[#6B7280] font-medium">Quick sends</span>
      </div>
      <div className="grid grid-cols-1 gap-2">
        {ACTIONS.map(a => {
          const Icon = a.Icon;
          const active = busy === a.key;
          return (
            <button
              key={a.key}
              onClick={() => handleClick(a)}
              disabled={active}
              className="w-full flex items-center gap-2.5 px-3 py-2 border border-transparent hover:border-[#E6EBF1] hover:bg-[#F6F9FC] rounded-lg text-sm text-[#374151] text-left transition disabled:opacity-50"
              data-testid={a.testid}
            >
              {active ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5 text-[#6B7280]" />}
              {a.label}
              <span className="ml-auto text-[10px] text-[#9CA3AF] font-mono">↩</span>
            </button>
          );
        })}
      </div>
      <div className="mt-3 pt-3 border-t border-[#F3F4F6] text-[11px] text-[#9CA3AF]">
        Recipient: {loan?.attorney_email ? <code className="text-[#374151]">{loan.attorney_email}</code> : <span className="italic">no attorney on file</span>}
      </div>

      {/* Inline info-needed prompt for Request Documents */}
      {askInfo && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" onClick={() => setAskInfo(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200">
              <h3 className="font-semibold text-slate-900">{askInfo.label}</h3>
              <p className="text-xs text-slate-500 mt-0.5">What are you requesting?</p>
            </div>
            <div className="px-6 py-4">
              <textarea value={infoText} onChange={e => setInfoText(e.target.value)} rows={3}
                placeholder="e.g. Updated police report + demand letter"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                data-testid="info-needed-input" />
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setAskInfo(null)} className="px-4 py-2 text-sm text-slate-600">Cancel</button>
              <button
                onClick={() => { const a = askInfo; setAskInfo(null); fire(a, { info_needed: infoText || "the item we discussed" }); }}
                disabled={!infoText.trim()}
                className="px-4 py-2 bg-[#0066FF] text-white text-sm rounded-lg disabled:opacity-50"
                data-testid="info-needed-send">
                Send
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Prompt for recipient if attorney email missing */}
      {askEmail && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" onClick={() => setAskEmail(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200">
              <h3 className="font-semibold text-slate-900">Recipient email needed</h3>
              <p className="text-xs text-slate-500 mt-0.5">This loan doesn&apos;t have an attorney email on file yet.</p>
            </div>
            <div className="px-6 py-4">
              <input type="email" value={emailInput} onChange={e => setEmailInput(e.target.value)}
                placeholder="attorney@examplelaw.com"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                data-testid="prompt-email-input" />
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setAskEmail(null)} className="px-4 py-2 text-sm text-slate-600">Cancel</button>
              <button
                onClick={() => { const a = askEmail; setAskEmail(null); fire(a, {}, emailInput); }}
                disabled={!emailInput.includes("@")}
                className="px-4 py-2 bg-[#0066FF] text-white text-sm rounded-lg disabled:opacity-50"
                data-testid="prompt-email-send">
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
